import { useMemo } from "react";
import { PLOT_TAGS, PLOT_OWNERS, getMeasureOwner } from "../config/plotGroups";
import { PLOTS } from "../config/plots";
import SearchableSelect from "./SearchableSelect";

interface GroupFilterProps {
  selectedTag: string | null;
  onTagChange: (tag: string | null) => void;
  selectedOwner: string | null;
  onOwnerChange: (owner: string | null) => void;
  outlierPlotIds: Set<string>;
  outlierOnly: boolean;
  onOutlierToggle: () => void;
}

export default function GroupFilter({
  selectedTag,
  onTagChange,
  selectedOwner,
  onOwnerChange,
  outlierPlotIds,
  outlierOnly,
  onOutlierToggle,
}: GroupFilterProps) {
  const outlierPlots = useMemo(() => PLOTS.filter(p => outlierPlotIds.has(p.id)), [outlierPlotIds]);

  const ownerCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const owner of PLOT_OWNERS) {
      counts.set(owner, outlierPlots.filter(p => getMeasureOwner(p.measureId) === owner).length);
    }
    return counts;
  }, [outlierPlots]);

  const ownerOptions = useMemo(() => {
    const opts = [{ value: "", label: "All owners" }];
    for (const owner of PLOT_OWNERS) {
      const count = ownerCounts.get(owner) ?? 0;
      opts.push({
        value: owner,
        label: count > 0 ? `${owner} (${count})` : owner,
      });
    }
    return opts;
  }, [ownerCounts]);

  return (
    <div className="group-filter">
      <div className="group-filter__group">
        <span className="group-filter__label">Tags</span>
        <button
          type="button"
          className={`group-filter__chip ${selectedTag === null ? "group-filter__chip--active" : ""}`}
          onClick={() => onTagChange(null)}
        >
          All
        </button>
        {PLOT_TAGS.map(tag => (
          <button
            type="button"
            key={tag}
            className={`group-filter__chip ${selectedTag === tag ? "group-filter__chip--active" : ""}`}
            onClick={() => onTagChange(selectedTag === tag ? null : tag)}
          >
            {tag}
          </button>
        ))}
      </div>

      <div className="group-filter__group">
        <span className="group-filter__label">Owner</span>
        <SearchableSelect
          value={selectedOwner ?? ""}
          options={ownerOptions}
          onChange={v => onOwnerChange(v === "" ? null : v)}
          placeholder="All owners"
          className="group-filter__owner-select"
        />
      </div>

      <div className="group-filter__group group-filter__group--right">
        <span className="group-filter__label">Outliers only</span>
        <button
          type="button"
          className={`group-filter__outlier-toggle ${outlierOnly ? "group-filter__outlier-toggle--active" : ""}`}
          onClick={onOutlierToggle}
          aria-pressed={outlierOnly}
        >
          <span className="group-filter__outlier-toggle-knob" />
        </button>
        <span className="group-filter__outlier-count">{outlierPlotIds.size}</span>
      </div>
    </div>
  );
}
